import type { TFunction } from 'i18next';
import { useState } from 'react';
import { Trash } from 'react-bootstrap-icons';

import { MFA_FACTOR, SMS_MFA_FEATURE_FLAG } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import passkeySetupIntro from './assets/passkey-setup-intro.svg';
import {
  ManageActionRow,
  MfaManageLayout,
  MfaPrimaryChoiceList,
  MfaRemoveConfirmDialog,
  type NewPrimaryMethod,
} from './mfa-authenticator-manage';
import { figmaReplacementChoices, formatMfaFactorDate } from './mfa-authenticator-manage-utils';
import { MfaHero } from './mfa-hero';
import { resolvePrimaryFactor } from './mfa-method-picker-utils';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

export type MfaPasskeyCredentialItem = {
  enrolledAt: string;
  id: string;
  lastUsedAt?: string | null;
  name: string;
};

function remainingPasskeyPrimaryChoices(enrolled: readonly string[]): NewPrimaryMethod[] {
  const choices: NewPrimaryMethod[] = [];
  if (enrolled.includes(MFA_FACTOR.Totp)) {
    choices.push('totp');
  }
  if (enrolled.includes(MFA_FACTOR.Sms)) {
    choices.push('sms');
  }
  if (enrolled.includes(MFA_FACTOR.Email)) {
    choices.push('email');
  }
  return choices;
}

function PasskeyMeta({ label, value }: { label: string; value: string }) {
  return (
    <SDSFlexbox fullWidth gap="text-small">
      <SDSText color="muted" text={label} type="body-small" />
      <SDSText text={value} type="body-small" />
    </SDSFlexbox>
  );
}

function PasskeyCard({
  onDelete,
  passkey,
  t,
}: {
  onDelete: (passkey: MfaPasskeyCredentialItem) => void;
  passkey: MfaPasskeyCredentialItem;
  t: TFunction<'common'>;
}) {
  const addedOn = formatMfaFactorDate(passkey.enrolledAt);
  const lastUsedOn = formatMfaFactorDate(passkey.lastUsedAt);
  return (
    <SDSFlexbox
      alignItems="flex-start"
      className="MfaPasskeyCard"
      dataTestId={`${MFA.PASSKEY_CARD}-${passkey.id}`}
      fullWidth
      justifyContent="space-between"
    >
      <SDSFlexbox direction="column" fullWidth gap="text-small">
        <SDSText text={passkey.name} type="body-medium" />
        {addedOn ? <PasskeyMeta label={t('security.mfa.added')} value={addedOn} /> : null}
        {lastUsedOn ? <PasskeyMeta label={t('security.mfa.lastUsed')} value={lastUsedOn} /> : null}
      </SDSFlexbox>
      <SDSPrimitiveButton
        ariaLabel={t('security.mfa.removePasskey')}
        dataTestId={`${MFA.PASSKEY_DELETE}-${passkey.id}`}
        onClick={() => onDelete(passkey)}
      >
        <SDSIcon color="text-secondary" icon={<Trash />} size="small" />
      </SDSPrimitiveButton>
    </SDSFlexbox>
  );
}

export function MfaPasskeyManageDialog({
  accountPrimary,
  credentials,
  enrolled,
  isMobile,
  onAddAnother,
  onClose,
  onRemoveAll,
  onRemoveCredential,
  onRemoveLast,
  t,
}: {
  accountPrimary?: string;
  credentials: readonly MfaPasskeyCredentialItem[];
  enrolled: readonly string[];
  isMobile: boolean;
  onAddAnother: () => void;
  onClose: () => void;
  onRemoveAll: () => void;
  onRemoveCredential: (credentialId: string) => void;
  onRemoveLast: () => void;
  t: TFunction<'common'>;
}) {
  const title = t('security.mfa.managePasskeysTitle');
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Passkey;
  const deletePasskey = (passkey: MfaPasskeyCredentialItem) => {
    if (credentials.length <= 1) {
      onRemoveLast();
      return;
    }
    onRemoveCredential(passkey.id);
  };
  const list = (
    <SDSFlexbox className="MfaPasskeyList" direction="column" fullWidth gap="text-medium">
      {credentials.map(passkey => (
        <PasskeyCard key={passkey.id} onDelete={deletePasskey} passkey={passkey} t={t} />
      ))}
    </SDSFlexbox>
  );
  const actions = (
    <div className="MfaAuthenticatorManage__actions">
      <ManageActionRow
        description={t('security.mfa.addAnotherPasskeyDescription')}
        onClick={onAddAnother}
        testId={MFA.PASSKEY_ADD_ANOTHER}
        title={t('security.mfa.addAnotherPasskey')}
      />
      <ManageActionRow
        danger
        description={isPrimary ? t('security.mfa.removeTotpChoosePrimaryFirst') : undefined}
        onClick={onRemoveAll}
        testId={MFA.PASSKEY_REMOVE_ALL}
        title={t('security.mfa.removeAllPasskeys')}
      />
    </div>
  );

  return (
    <MfaManageLayout
      actions={actions}
      className="MfaTotpDialog MfaAuthenticatorManageDialog MfaSmsManageDialog"
      closeTestId={MFA.PASSKEY_CLOSE}
      dataTestId={MFA.PASSKEY_MANAGE}
      hero={<MfaHero plain={isMobile} setup src={passkeySetupIntro} />}
      isMobile={isMobile}
      onClose={onClose}
      t={t}
      title={title}
    >
      {list}
    </MfaManageLayout>
  );
}

export function MfaPasskeyRemoveDialog({
  accountPrimary,
  email,
  enrolled,
  isMobile,
  onBack,
  onClose,
  onConfirm,
  passkeyCount,
  smsPhoneMasked,
  t,
}: {
  accountPrimary?: string;
  email?: string;
  enrolled: readonly string[];
  isMobile: boolean;
  onBack: () => void;
  onClose: () => void;
  onConfirm: (replacement?: NewPrimaryMethod) => void;
  passkeyCount?: number;
  smsPhoneMasked?: string | null;
  t: TFunction<'common'>;
}) {
  const smsSetupAvailable = useFeatureFlag(SMS_MFA_FEATURE_FLAG);
  const remaining = remainingPasskeyPrimaryChoices(enrolled);
  const isPrimary = resolvePrimaryFactor(enrolled, accountPrimary) === MFA_FACTOR.Passkey;
  const needsPrimary = isPrimary;
  const [selected, setSelected] = useState<NewPrimaryMethod | undefined>();
  const canRemove = needsPrimary ? Boolean(selected) : true;
  const confirmRemove = () => {
    if (needsPrimary) {
      if (!selected) {
        return;
      }
      onConfirm(selected);
      return;
    }
    onConfirm();
  };
  const primaryChoices = figmaReplacementChoices('passkey', t, {
    email: remaining.includes('email') ? email : undefined,
    passkeyCount: passkeyCount ?? 0,
    smsOffered: enrolled.includes(MFA_FACTOR.Sms) || smsSetupAvailable,
    smsPhoneMasked: enrolled.includes(MFA_FACTOR.Sms) ? (smsPhoneMasked ?? undefined) : undefined,
    totpEnrolled: remaining.includes('totp'),
  });
  const primaryField = needsPrimary ? (
    <MfaPrimaryChoiceList
      name="mfa-passkey-new-primary"
      onSelect={setSelected}
      options={primaryChoices}
      selected={selected}
    />
  ) : null;

  return (
    <MfaRemoveConfirmDialog
      backLabel={t('security.mfa.backToSecurity')}
      cancelLabel={t('security.mfa.cancel')}
      confirmDisabled={!canRemove}
      confirmLabel={needsPrimary ? t('security.mfa.setPrimaryAndRemove') : t('security.mfa.removePasskey')}
      dataTestId={MFA.PASSKEY_REMOVE_CONFIRM}
      dialogClassName="MfaTotpDialog MfaRemoveConfirmDialog"
      hero={<MfaHero plain={isMobile} setup src={passkeySetupIntro} />}
      isMobile={isMobile}
      lead={
        needsPrimary ? t('security.mfa.removePasskeysChoosePrimaryLead') : t('security.mfa.removePasskeysDescription')
      }
      onBack={onBack}
      onClose={onClose}
      onConfirm={confirmRemove}
      title={t('security.mfa.removePasskeysTitle')}
    >
      {primaryField}
    </MfaRemoveConfirmDialog>
  );
}
